import { Component, effect, HostListener, inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router, RouterOutlet } from "@angular/router";
import type { HubConnection } from "@microsoft/signalr";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { Subscription, take } from "rxjs";
import { SignalRService } from "./real-time-notifications/signalr-service/signalr.service";
import { RoutingParentSyncService } from "./routing-utils/routing-parent-sync.service";
import { AuthService, BroadcastService, CardViewService, HelpersService, UsersService } from "./shared";
import { IframeMessage, IframeMessagesService } from "./shared/iframe-messages-service/iframe-messages.service";
import { JumpstartComponentsWkIconsModule } from "@wk/components-v3-angular17";
import { WkIconUser2SpritePds3 } from "./shared/icons/wk-icon-user-sprite-pds3/index";
import { AppLoaderComponent } from "./shared/components/app-loader/app-loader.component";
import { NotificationComponent } from "./notification/notification.component";
import { LanguageService } from "@services/language.service";

/**
 * App module TS
 */
@Component({
    selector: "wk-app-root",
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss",
    encapsulation: ViewEncapsulation.None,
    imports: [JumpstartComponentsWkIconsModule, WkIconUser2SpritePds3, RouterOutlet, AppLoaderComponent, NotificationComponent, TranslatePipe],
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly authService = inject(AuthService);
    private readonly cardViewService = inject(CardViewService);
    private readonly helpersService = inject(HelpersService);
    private readonly languageService = inject(LanguageService);
    private readonly router = inject(Router);
    private readonly routingSync = inject(RoutingParentSyncService);
    private readonly signalRService = inject(SignalRService);
    private readonly titleService = inject(Title);
    private readonly translateService = inject(TranslateService);
    readonly usersService = inject(UsersService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    subscriptions: Subscription[] = [];
    suppressSpinnerSubscription: Subscription;
    private unauthorizedNavSubscription: Subscription;
    private noUdsNavSubscription: Subscription;
    signalRInitiated = false;
    showNavigationDenied = false;
    showNavigationUnauthorized = false;
    sessionInitialized = false;
    isFromSettingsPage = false;

    constructor() {
        // Hide the loader text if the iframe url is from settings page
        // Not using the Router url is initially set to "/" in the appComponent constructor and ngOnInit because navigation has not completed.
        // the correct url, available as urlAfterRedirects, is only available after the Router NavigationEnd event is completed.
        // this causes the loader text to briefly appear until navigation finishes.
        // Using window.location.href to determine if we are in the settings page as a workaround.
        this.isFromSettingsPage = window?.self?.location?.href?.includes("/settings/");

        this.translateService.setFallbackLang(this.languageService.getLang());
        this.registerStringBroadcast();
        effect(() => {
            if (this.signalRService.signalRConnected()) {
                this.listenToSignalRMessages();
            } else {
                this.stopListeningToSignalRMessages();
            }
        });
        this.routingSync.init();
        this.unauthorizedNavSubscription = this.helpersService.unauthorizedNavigationEvent$.subscribe(() => {
            this.showNavigationDenied = true;
        });
        this.noUdsNavSubscription = this.helpersService.noUdsNavigationEvent$.subscribe(() => {
            this.showNavigationUnauthorized = true;
        });
        effect(() => {
            const session = this.authService.sessionData();
            this.sessionInitialized = session?.sessionName != null;
        });
    }

    ngOnInit() {
        this.translateService
            .get("General.Title")
            .pipe(take(1))
            .subscribe((string) => {
                this.titleService.setTitle(string);
            });
        this.suppressSpinnerSubscription = this.helpersService.suppressSpinnerEvent$.subscribe(() => {
            this.sessionInitialized = true;
        });
    }

    listenToSignalRMessages(): boolean {
        if (this.signalRInitiated) {
            return false;
        }
        this.signalRInitiated = true;
        const connection: HubConnection = this.signalRService.connection;

        connection.on("user-updated", async (userId, userData) => {
            if (typeof userData === "string") {
                userData = JSON.parse(userData);
            }

            if (userData) {
                this.usersService.userChangedSubject.next({
                    userId: userId,
                    userData,
                });
            }

            const user = this.usersService.getUserById(userId);
            if (user && user.Active !== userData.Active) {
                user.Active = userData.Active;
                this.helpersService.refreshFilters();
            }
        });

        connection.on("dashboard-updated", () => {
            this.cardViewService.setCurrentViewType(this.cardViewService.currentViewType);
            this.cardViewService.refreshCardsViewLabelsSubject.next(false);
        });
    }

    stopListeningToSignalRMessages(): boolean {
        if (!this.signalRInitiated) {
            return false;
        }
        this.signalRInitiated = false;
        const connection: HubConnection = this.signalRService.connection;
        if (connection) {
            connection.off("user-updated");
            connection.off("user-dashboard-setting-updated");
            connection.off("user-dashboard-setting-deleted");
        }
    }

    ngOnDestroy() {
        this.suppressSpinnerSubscription.unsubscribe();
        this.unauthorizedNavSubscription?.unsubscribe();
        this.noUdsNavSubscription?.unsubscribe();

        if (this.subscriptions.length > 0) {
            this.unregisterStringBroadcast();
        }
    }

    registerStringBroadcast() {
        this.subscriptions.push(
            BroadcastService.on<string>("navigate-to").subscribe((url) => {
                this.router.navigateByUrl(url);
            }),
        );

        this.subscriptions.push(
            BroadcastService.on<IframeMessage>("setLanguage").subscribe((message) => {
                this.iframeMessagesService.postMessage("languageInitialized", null);
                // Also switches ngx-translate, so an unsupported host language no longer desyncs the two.
                this.languageService.setLang(message.data.language);
                const lang = this.languageService.getLang();
                this.translateService
                    .get("General.Title")
                    .pipe(take(1))
                    .subscribe((string) => {
                        this.titleService.setTitle(string);
                    });
                BroadcastService.broadcast("lang-change", lang);
            }),
        );
    }

    unregisterStringBroadcast() {
        let index = 0;

        while (this.subscriptions.length > index) {
            this.subscriptions[index].unsubscribe();
            index++;
        }
        this.subscriptions = [];
    }

    @HostListener("document:keydown.escape") onKeydownHandler() {
        this.showNavigationDenied = false;
    }
}
